import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { dispatch } from '../../utils/event-helpers';
import {
  VerticalTabsController,
  type TabPanelLike,
  type VerticalTabsHost,
} from './vertical-tabs.controller';

export type VerticalTabsVariant = 'line' | 'button';

/**
 * Vertical tabbed interface managing a list of `asc-tab-panel` elements.
 *
 * Renders a scrollable trigger list on the left and an associated content
 * region on the right. Enforces single-active-tab behavior, propagates the
 * `variant` to all child panels, and manages ARIA tablist semantics and
 * keyboard roving focus.
 *
 * @element asc-vertical-tabs
 *
 * @fires asc-change - Fired after any tab activation. Detail: `{ activePanel: HTMLElement; activeIndex: number }`
 *
 * @slot - Default slot. Accepts `asc-tab-panel` elements only.
 *
 * @csspart base - Root two-column layout container.
 * @csspart tablist - The `role="tablist"` div forming the left column.
 * @csspart tab - Each proxy `<button role="tab">` in the tablist.
 * @csspart tab-label - Text span inside each proxy tab button.
 * @csspart tab-description - Description text span inside each proxy tab button; hidden when no description is provided.
 * @csspart tab-count - Count badge span inside each proxy tab button.
 * @csspart content - Right-side panel projection area wrapping the default slot.
 *
 * @cssprop --asc-vertical-tabs-tablist-width - Width of the tab trigger column. Default: `220px`
 * @cssprop --asc-vertical-tabs-tablist-max-width - Max width of the tab trigger column when descriptions are present. Default: `560px`
 * @cssprop --asc-vertical-tabs-gap - Gap between tablist and content. Default: `var(--asc-spacing-4xl)`
 * @cssprop --asc-vertical-tabs-tablist-bg - Tablist column background. Default: `transparent`
 * @cssprop --asc-vertical-tabs-content-bg - Content area background. Default: `transparent`
 * @cssprop --asc-vertical-tabs-tab-text-color - Default tab text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-vertical-tabs-tab-bg-hover - Tab hover background. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-vertical-tabs-tab-padding-x - Tab horizontal padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-vertical-tabs-tab-padding-y - Tab vertical padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-vertical-tabs-tab-disabled-text-color - Disabled tab text color. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-vertical-tabs-count-bg - Count badge background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-vertical-tabs-count-text-color - Count badge text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-vertical-tabs-count-border-radius - Count badge border radius. Default: `var(--asc-radius-full)`
 * @cssprop --asc-vertical-tabs-active-line-color - Active accent border (line variant). Default: `var(--asc-fg-brand-primary)`
 * @cssprop --asc-vertical-tabs-active-line-width - Active accent line width. Default: `2px`
 * @cssprop --asc-vertical-tabs-active-bg - Active tab background (line variant). Default: `var(--asc-bg-brand-primary)`
 * @cssprop --asc-vertical-tabs-active-text-color - Active tab text color (line variant). Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-vertical-tabs-active-count-bg - Active count badge background. Default: `var(--asc-bg-brand-secondary)`
 * @cssprop --asc-vertical-tabs-active-count-text-color - Active count badge text color. Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-vertical-tabs-btn-active-bg - Active tab background (button variant). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-vertical-tabs-btn-active-border-color - Active tab border color (button variant). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-vertical-tabs-btn-border-radius - Active tab border radius (button variant). Default: `var(--asc-radius-sm)`
 * @cssprop --asc-vertical-tabs-btn-active-text-color - Active tab text color (button variant). Default: `var(--asc-text-primary)`
 * @cssprop --asc-vertical-tabs-tab-description-text-color - Description text color. Default: `var(--asc-text-tertiary)`
 *
 * @prop {boolean} centerTabs - When `true`, vertically centers the tab trigger list against the panel content area.
 *   Useful when the tab count is low but panel content is tall, preventing an empty gap below the last tab.
 *   Panel-height stabilization is always enabled regardless of this prop.
 */
@customElement('asc-vertical-tabs')
export class AscVerticalTabs extends LitElement implements VerticalTabsHost {
  static override styles = css`
    :host {
      display: block;
    }

    /* ── Base two-column layout ────────────────────────────────── */
    [part='base'] {
      display: flex;
      gap: var(--asc-vertical-tabs-gap, var(--asc-spacing-4xl));
    }
    /* ── Tablist column ───────────────────────────────────────── */
    [part='tablist'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-spacing-md);
      width: var(--asc-vertical-tabs-tablist-width, 220px);
      flex-shrink: 0;
      background: var(--asc-vertical-tabs-tablist-bg, transparent);
    }

    /* When any tab has a description, grow the tablist up to 560px on desktop */
    :host([has-description]) [part='tablist'] {
      width: auto;
      flex: 1 0 0;
      max-width: var(--asc-vertical-tabs-tablist-max-width, 560px);
    }

    /* Button variant: tablist gets a container wrapper */
    :host([variant='button']) [part='tablist'] {
      background: var(--asc-vertical-tabs-tablist-bg, var(--asc-bg-secondary));
      border: var(--asc-border-width) solid var(--asc-vertical-tabs-btn-active-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-vertical-tabs-btn-border-radius, var(--asc-radius-sm));
      padding: var(--asc-spacing-xs);
      gap: var(--asc-spacing-xs);
    }

    /* ── Proxy tab button ─────────────────────────────────────── */
    [part='tab'] {
      display: flex;
      align-items: flex-start;
      gap: var(--asc-spacing-md);
      width: 100%;
      padding:
        var(--asc-vertical-tabs-tab-padding-y, var(--asc-spacing-xs))
        var(--asc-vertical-tabs-tab-padding-x, var(--asc-spacing-lg));
      background: none;
      border: none;
      border-left: var(--asc-vertical-tabs-active-line-width, 2px) solid transparent;
      cursor: pointer;
      color: var(--asc-vertical-tabs-tab-text-color, var(--asc-text-tertiary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-sm);
      text-align: left;
    }

    [part='tab']:hover:not(:disabled) {
      background-color: var(
        --asc-vertical-tabs-tab-bg-hover,
        var(--asc-bg-primary_hover)
      );
    }

    [part='tab']:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* Active tab — line variant */
    [part='tab'][aria-selected='true'] {
      border-left-color: var(
        --asc-vertical-tabs-active-line-color,
        var(--asc-fg-brand-primary)
      );
      background-color: var(--asc-vertical-tabs-active-bg, var(--asc-bg-brand-primary));
      color: var(--asc-vertical-tabs-active-text-color, var(--asc-text-brand-secondary));
    }

    /* Button variant tab buttons */
    :host([variant='button']) [part='tab'] {
      border-left: none;
      border-radius: var(--asc-vertical-tabs-btn-border-radius, var(--asc-radius-sm));
      padding:
        var(--asc-vertical-tabs-tab-padding-y, var(--asc-spacing-md))
        var(--asc-vertical-tabs-tab-padding-x, var(--asc-spacing-lg));
      overflow: hidden;
      position: relative;
    }

    :host([variant='button']) [part='tab'][aria-selected='true'] {
      background-color: var(--asc-vertical-tabs-btn-active-bg, var(--asc-bg-primary));
      color: var(--asc-vertical-tabs-btn-active-text-color, var(--asc-text-primary));
      box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
    }

    /* Disabled tab */
    [part='tab']:disabled {
      cursor: not-allowed;
      color: var(
        --asc-vertical-tabs-tab-disabled-text-color,
        var(--asc-text-disabled)
      );
    }

    [part='tab']:disabled:hover {
      background: none;
    }

    /* ── Tab label ────────────────────────────────────────────── */
    [part='tab-label'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-spacing-xs);
      flex: 1 0 0;
      min-width: 0;
      font-size: var(--asc-font-size-text-lg);
    }

    /* ── Tab description ──────────────────────────────────────── */
    [part='tab-description'] {
      font-size: var(--asc-font-size-text-md);
      font-weight: var(--asc-font-weight-regular);
      line-height: var(--asc-line-height-text-md);
      color: var(--asc-vertical-tabs-tab-description-text-color, var(--asc-text-tertiary));
      overflow-wrap: anywhere;
    }

    /* ── Tab count badge ──────────────────────────────────────── */
    [part='tab-count'] {
      display: inline-flex;
      align-items: center;
      padding: var(--asc-spacing-xxs) var(--asc-spacing-md);
      background-color: var(--asc-vertical-tabs-count-bg, var(--asc-bg-primary));
      color: var(--asc-vertical-tabs-count-text-color, var(--asc-text-primary));
      border: var(--asc-border-width) solid var(--asc-border-primary);
      border-radius: var(--asc-vertical-tabs-count-border-radius, var(--asc-radius-full));
      font-size: var(--asc-font-size-text-xs);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-xs);
      flex-shrink: 0;
    }

    [part='tab'][aria-selected='true'] [part='tab-count'] {
      background-color: var(
        --asc-vertical-tabs-active-count-bg,
        var(--asc-bg-brand-secondary)
      );
      color: var(
        --asc-vertical-tabs-active-count-text-color,
        var(--asc-text-brand-secondary)
      );
      border-color: transparent;
    }

    /* ── Content area ─────────────────────────────────────────── */
    [part='content'] {
      flex: 1 1 auto;
      min-width: 0;
      background: var(--asc-vertical-tabs-content-bg, transparent);
    }

    /* ── Center-tabs alignment ───────────────────────────────── */
    :host([center-tabs]) [part='tablist'] {
      align-self: center;
    }

    /* ── Hidden utility ───────────────────────────────────────── */
    [hidden] {
      display: none !important;
    }

  `;

  private _controller = new VerticalTabsController(this);

  /** Visual style applied to the tab list triggers and propagated to child panels. */
  @property({ type: String, reflect: true }) variant: VerticalTabsVariant = 'line';

  /** Accessible label for the tablist region. */
  @property({ type: String }) label = '';

  /**
   * When `true`, vertically centers the tab trigger list against the panel content
   * area. Useful when the tab count is low but panel content is tall, preventing
   * an empty gap below the last tab.
   */
  @property({ type: Boolean, attribute: 'center-tabs', reflect: true }) centerTabs = false;

  @query('[part="content"]') private _contentEl!: HTMLElement;

  @state() private _panels: TabPanelLike[] = [];

  private _contentObserver: ResizeObserver | null = null;
  private _maxContentHeight = 0;
  private _isPreMeasuring = false;

  private async _primeTallestContentHeight(): Promise<void> {
    if (this._isPreMeasuring || !this._contentEl || this._panels.length === 0) return;

    this._isPreMeasuring = true;
    const panels = [...this._panels];
    const originalActive = panels.map(panel => panel.active);
    const originalVisibility = this._contentEl.style.visibility;
    let maxHeight = this._maxContentHeight;

    // Prevent visual flicker while each panel is measured at the current viewport.
    this._contentEl.style.visibility = 'hidden';

    for (let i = 0; i < panels.length; i++) {
      panels.forEach((panel, index) => {
        panel.active = index === i;
      });

      this._panels = [...panels];
      await this.updateComplete;

      const panelEl = (panels[i] as HTMLElement).shadowRoot?.querySelector<HTMLElement>('[part="panel"]');
      if (!panelEl) continue;

      const renderedHeight = panelEl.getBoundingClientRect().height;
      if (renderedHeight > maxHeight) {
        maxHeight = renderedHeight;
      }
    }

    // Restore original active state after pre-measurement.
    panels.forEach((panel, index) => {
      panel.active = originalActive[index];
    });

    this._panels = [...panels];
    await this.updateComplete;

    this._maxContentHeight = maxHeight;
    if (this._maxContentHeight > 0) {
      this._contentEl.style.minHeight = `${this._maxContentHeight}px`;
    }

    this._contentEl.style.visibility = originalVisibility;
    this._isPreMeasuring = false;
  }

  private _updateTallestContentHeight(): void {
    if (!this._contentEl) return;

    // Measure only the currently active panel's rendered height.
    // Using scrollHeight on collapsed/inert panels can over-report image-heavy content.
    const activePanel = this._panels.find(panel => panel.active);
    const panelEl = activePanel
      ? (activePanel as HTMLElement).shadowRoot?.querySelector<HTMLElement>('[part="panel"]')
      : null;

    if (!panelEl) return;

    const renderedHeight = panelEl.getBoundingClientRect().height;
    if (renderedHeight <= 0) return;

    if (renderedHeight > this._maxContentHeight) {
      this._maxContentHeight = renderedHeight;
    }

    if (this._maxContentHeight > 0) {
      this._contentEl.style.minHeight = `${this._maxContentHeight}px`;
    }
  }

  private _setupContentObserver(): void {
    if (this._contentObserver || !this._contentEl) return;
    this._contentObserver = new ResizeObserver(() => {
      this._updateTallestContentHeight();
    });
    this._contentObserver.observe(this._contentEl);
  }

  private _teardownContentObserver(): void {
    this._contentObserver?.disconnect();
    this._contentObserver = null;
  }

  override firstUpdated(): void {
    this._setupContentObserver();
    this._updateTallestContentHeight();
    void this._primeTallestContentHeight();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._teardownContentObserver();
  }

  private _syncPanels(): void {
    const panels = this._controller.syncPanels();
    this._panels = panels;
    const hasDesc = panels.some(p => !!this._controller.getDescriptionText(p));
    this.toggleAttribute('has-description', hasDesc);
    this._updateTallestContentHeight();
    void this._primeTallestContentHeight();
  }

  private _activatePanel(index: number): void {
    const panels = this._panels;
    if (!panels[index] || panels[index].disabled) return;
    panels.forEach((p, i) => { p.active = i === index; });
    this._panels = [...panels];
    this._updateTallestContentHeight();
    dispatch<{ activePanel: HTMLElement; activeIndex: number }>(
      this, 'asc-change', { detail: { activePanel: panels[index], activeIndex: index } },
    );
  }

  private _handleSlotChange(): void {
    this._syncPanels();
  }

  private _handleAscActivate(e: Event): void {
    const target = e.target as TabPanelLike;
    const index = this._panels.indexOf(target);
    if (index !== -1) this._activatePanel(index);
  }

  private _handleTabClick(index: number): void {
    this._activatePanel(index);
    // Focus the proxy button after activation
    const buttons = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLButtonElement>('[part="tab"]') ?? [],
    );
    buttons[index]?.focus();
  }

  private _handleTabKeyDown(e: KeyboardEvent): void {
    const panels = this._panels;
    const buttons = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLButtonElement>('[part="tab"]') ?? [],
    );
    const current = buttons.indexOf(e.target as HTMLButtonElement);
    if (current === -1) return;

    const findNonDisabled = (start: number, direction: number): number => {
      let i = (start + direction + panels.length) % panels.length;
      let steps = panels.length;
      while (panels[i].disabled && steps-- > 0) {
        i = (i + direction + panels.length) % panels.length;
      }
      return panels[i].disabled ? current : i;
    };

    let next;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        next = findNonDisabled(current, 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        next = findNonDisabled(current, -1);
        break;
      case 'Home': {
        e.preventDefault();
        // Find first non-disabled
        const first = panels.findIndex(p => !p.disabled);
        next = first !== -1 ? first : current;
        break;
      }
      case 'End': {
        e.preventDefault();
        // Find last non-disabled
        let last = current;
        for (let i = panels.length - 1; i >= 0; i--) {
          if (!panels[i].disabled) { last = i; break; }
        }
        next = last;
        break;
      }
      default:
        return;
    }

    this._activatePanel(next);
    buttons[next]?.focus();
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('variant')) {
      this._panels.forEach(p => { p.variant = this.variant; });
    }
    this._updateTallestContentHeight();
  }

  override render() {
    return html`
      <div part="base">
        <div
          part="tablist"
          role="tablist"
          aria-orientation="vertical"
          aria-label=${this.label || nothing}
          @keydown=${this._handleTabKeyDown}
        >
          ${this._panels.map((panel, i) => {
            const countText = this._controller.getCountText(panel);
            const descriptionText = this._controller.getDescriptionText(panel);
            return html`
              <button
                type="button"
                part="tab"
                role="tab"
                aria-selected="${panel.active}"
                aria-disabled=${panel.disabled ? 'true' : nothing}
                ?disabled=${panel.disabled}
                tabindex=${panel.active ? 0 : -1}
                data-index=${i}
                @click=${() => this._handleTabClick(i)}
              >
                <span part="tab-label">
                  ${this._controller.getTabNameText(panel)}
                  ${descriptionText
                    ? html`<span part="tab-description">${descriptionText}</span>`
                    : nothing}
                </span>
                ${countText
                  ? html`<span part="tab-count">${countText}</span>`
                  : nothing}
              </button>
            `;
          })}
        </div>

        <div part="content">
          <slot
            @slotchange=${this._handleSlotChange}
            @asc-activate=${this._handleAscActivate}
          ></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-vertical-tabs': AscVerticalTabs;
  }
}
